import { useEffect, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { paymentsAPI } from '../api/client';

const ordinal = (n: number) => {
  const rem10 = n % 10;
  const rem100 = n % 100;
  if (rem10 === 1 && rem100 !== 11) return `${n}st`;
  if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
  if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
  return `${n}th`;
};

// Admin control for the org-wide monthly billing day: every member's dues go
// out on this day of the month. Saving it re-aligns existing subscriptions'
// renewals onto the day (forward only), and new signups' first charge lands
// on the next occurrence.
export default function BillingDayCard() {
  const [loaded, setLoaded] = useState(false);
  const [savedDay, setSavedDay] = useState<number | null>(null);
  const [nextDate, setNextDate] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    paymentsAPI
      .getBillingConfig()
      .then((data) => {
        if (cancelled) return;
        setSavedDay(data.billing_day);
        setNextDate(data.next_payment_date);
        setDraft(data.billing_day != null ? String(data.billing_day) : '');
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (value: number | null) => {
    setSaving(true);
    setError('');
    try {
      const data = await paymentsAPI.setBillingConfig(value);
      setSavedDay(data.billing_day);
      setNextDate(data.next_payment_date);
      setDraft(data.billing_day != null ? String(data.billing_day) : '');
      if (data.billing_day != null && data.aligned_count > 0) {
        toast.success(
          `Billing day saved — ${data.aligned_count} member subscription${
            data.aligned_count > 1 ? 's' : ''
          } moved to the ${ordinal(data.billing_day)}.`,
        );
      }
    } catch {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const dirty = draft !== (savedDay != null ? String(savedDay) : '');

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-navy mb-1 flex items-center gap-2">
        <CalendarClock size={20} />
        Monthly Billing Day
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        The day of the month every member&apos;s dues go out. Saving moves
        existing renewals onto this day (always forward — nobody is charged
        earlier than their current renewal), and new signups&apos; first charge
        lands on the next occurrence. Skipped months and one-time charges are
        unaffected.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold bg-white"
          aria-label="Billing day of the month"
        >
          <option value="">On each member&apos;s signup date</option>
          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              The {ordinal(d)} of every month
            </option>
          ))}
        </select>
        <button
          onClick={() => save(draft === '' ? null : Number(draft))}
          disabled={saving || !dirty}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy-light transition text-sm font-medium disabled:opacity-50"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          Save
        </button>
        {savedDay != null && nextDate && !dirty && (
          <span className="text-sm text-gray-500">
            Next payment date:{' '}
            <span className="font-medium text-navy">
              {new Date(`${nextDate}T12:00:00`).toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </span>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
